import DoneAllIcon from '@mui/icons-material/DoneAll'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import clsx from 'clsx'
import React, { useMemo } from 'react'
import iconoDoc from '../../../../../../../assets/iconosVinculaciones/3.svg'
import { Text } from '../../../../../ui'
import { formatCurrency, formatDate } from '../../../../../../utils/formatter'

const TablaDocumento = ({
  rows,
  dataModal,
  tipoPago,
  onCurrentData,
  selectedCheckboxes,
  setSelectedCheckboxes,
  setMontoGestion,
  extraRowData,
  modalData,
  setModalData,
  setShowModalCaja
}) => {
  const extraRow = useMemo(() => {
    if (!dataModal) return null

    return {
      id: 'custom-caja-principal',
      fecha: modalData?.fecha,
      nombreCuentaBanco: modalData?.razonSocial,
      numeroCuentaBanco: modalData?.rut,
      descripcion: modalData?.descripcion,
      folio: modalData?.folio,
      montoAbono: modalData?.monto,
      montoCargo: Math.abs(dataModal.montoTotal),
      status: 'PENDIENTE',
      customRow: true
    }
  }, [dataModal, modalData])

  const combinedRows = useMemo(() => {
    if (extraRow) return [extraRow]
    return []
  }, [extraRow])

  const getSafeDateLabel = value => {
    if (!value) return ''

    const parsedDate = new Date(value)
    if (Number.isNaN(parsedDate.getTime())) return ''

    return formatDate(value)
  }

  const styles = {
    wrapper: 'overflow-x-auto rounded-3xl bg-white shadow-sm',
    table: 'w-full text-[#000231] text-xs border-collapse leading-6 table-fixed bg-white',
    thead: 'sticky top-0 z-10 bg-gray-50',
    th: 'py-2 px-1 w-auto max-w-[120px] text-center text-[11px] break-words border-b border-gray-200 bg-gray-100',
    tbody: 'bg-gray-50',
    tr: 'border-b border-gray-200 bg-gray-50 hover:bg-gray-100/70',
    td: 'py-1 px-2 w-auto max-w-[200px] text-center break-words align-top',
    smallCol: 'w-auto max-w-[80px]',
    mediumCol: 'w-auto max-w-[140px]',
    wideCol: 'w-auto max-w-[300px]',
    boldText: 'font-bold text-[0.65rem]',
    text: 'text-[0.705rem] text-gray-500'
  }

  const orderableCols = [
    { col: '', field: null },
    { col: 'Fecha', field: 'fecha' },
    { col: 'Razón Social', field: 'razonSocial' },
    { col: 'Folio', field: 'folio' },
    { col: 'Tipo Documento', field: 'tipoDocumento' },
    { col: 'Monto Documento', field: 'abono' },
    { col: 'Monto Saldado', field: 'cargo' },
    { col: 'Status', field: 'status' },
    { col: 'Acción', field: 'accion' }
  ]

  React.useEffect(() => {
    if (extraRow) {
      setSelectedCheckboxes(prev => ({ ...prev, [extraRow.id]: true }))
      onCurrentData?.([extraRow])
    }
  }, [extraRow, onCurrentData, setSelectedCheckboxes])

  const handleCheckboxToggle = id => {
    const updated = {
      ...selectedCheckboxes,
      [id]: !selectedCheckboxes[id]
    }
    setSelectedCheckboxes(updated)

    const selectedRows = Object.entries(updated)
      .filter(([, isChecked]) => isChecked)
      .map(([key]) => combinedRows.find(r => r.id === key))
      .filter(Boolean)

    onCurrentData?.(selectedRows)
  }

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            {orderableCols.map(({ col }, i) => (
              <th key={i} className={styles.th}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {combinedRows.map((row, i) => {
            const isChecked = !!selectedCheckboxes[row.id]

            return (
              <tr key={i} className={clsx(styles.tr)}>
                <td className={clsx(styles.td, styles.smallCol)}>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      className="rounded"
                      onChange={() => handleCheckboxToggle(row.id)}
                    />
                    <img src={iconoDoc} className="min-w-8 h-8" />
                  </div>
                </td>
                <td className={styles.td}>
                  <Text className={styles.boldText}>{getSafeDateLabel(row.fecha)}</Text>
                </td>
                <td className={styles.td}>
                  <Text className={styles.boldText}>{row.nombreCuentaBanco}</Text>
                  <Text className={styles.text}>{row.numeroCuentaBanco}</Text>
                </td>
                <td className={styles.td}>
                  <Text className={styles.boldText}>{row.folio}</Text>
                </td>
                <td className={styles.td}>{row.descripcion}</td>
                <td className={clsx(styles.td, styles.mediumCol)}>
                  {formatCurrency(row.montoAbono)}
                </td>
                <td className={clsx(styles.td, styles.mediumCol)}>
                  {formatCurrency(row.montoCargo)}
                </td>
                <td className={clsx(styles.td, styles.mediumCol)}>
                  <Text
                    className={clsx(styles.boldText, 'tracking-wider', {
                      'text-accent3': row.status === 'PENDIENTE',
                      'text-accent2': row.status === 'CONCILIADO'
                    })}
                  >
                    {row.status}
                  </Text>
                </td>
                <td className={clsx(styles.td, styles.mediumCol, 'whitespace-nowrap')}>
                  <div className='flex h-full items-center justify-center space-x-2'>
                    <div className='flex min-w-[52px] flex-col items-center justify-center text-[10px] text-gray-500 hover:cursor-pointer'>
                      <button
                        type='button'
                        className={clsx(
                          'flex h-7 w-7 items-center justify-center rounded-full shadow-full-xs transition-colors duration-150',
                          {
                            'cursor-pointer bg-accent2 text-white hover:bg-accent2/90': isChecked,
                            'cursor-pointer bg-gray-100 text-gray-300 hover:bg-gray-200': !isChecked
                          }
                        )}
                        onClick={() => handleCheckboxToggle(row.id)}
                      >
                        <DoneAllIcon className='h-4 w-4' />
                      </button>
                      <Text className='mt-1 whitespace-nowrap'>
                        {isChecked ? 'Seleccionado' : 'Seleccionar'}
                      </Text>
                    </div>
                    <div className='flex min-w-[40px] flex-col items-center justify-center text-[10px] text-gray-500 hover:cursor-pointer'>
                      <button
                        type='button'
                        aria-label='Editar documento de referencia'
                        onClick={() => {
                          setModalData({
                            ...modalData,
                            razonSocial: modalData?.razonSocial || '',
                            rut: modalData?.rut || '',
                            fecha: modalData?.fecha || '',
                            descripcion: modalData?.descripcion || '',
                            monto: modalData?.monto || 0,
                            tipoObligacion: modalData?.tipoObligacion || 'pago-anterior'
                          })
                          setShowModalCaja(true)
                        }}
                        className='flex h-7 w-7 items-center justify-center rounded-full shadow-full-xs'
                      >
                        <EditOutlinedIcon sx={{ fontSize: 16, color: 'inherit' }} />
                      </button>
                      <Text className='mt-1 whitespace-nowrap'>Editar</Text>
                    </div>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default TablaDocumento
